import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import api, { handleAxiosError } from '../config/api'
import { executeRecaptcha } from '../utils/recaptcha'
import { useTranslation } from 'react-i18next'
import LanguageToggle from '../components/LanguageToggle'
import { normalizeIndianMobile } from '../utils/authValidation'
import {
  applyServerRateLimit,
  AUTH_RATE_LIMIT,
  consumeRateLimit,
} from '../utils/clientRateLimit'
import {
  trackLoginStart,
  trackLoginSubmit,
  trackOtpRequested,
  trackOtpResend,
  trackOtpTimeout,
  trackLoginOtpWrong,
  trackLoginSuccess,
} from '../utils/ga4Events'
import TnCPopup from '../components/TnCPopup'
import { shouldRedirectToWelcomeScreen } from '../utils/profileRedirect'
import {
  resolveSecondLoginFlow,
  setJourneyFlowStage,
} from '../utils/journeyFlow'
import { redirectToRoute } from '../utils/routeRedirect'
import i18n from '../i18n'

function Login() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [phone, setPhone] = useState('+91 ')
  const [phoneError, setPhoneError] = useState('')
  const [otpVisible, setOtpVisible] = useState(false)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [otpError, setOtpError] = useState('')
  const [timeLeft, setTimeLeft] = useState(120)
  const [timerActive, setTimerActive] = useState(false)
  const [expired, setExpired] = useState(false)
  const [loggedIn, setLoggedIn] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const language = localStorage.getItem("appLanguage") || "en";

  const otpResendCountRef = useRef(0);
  const loginOtpWrongCountRef = useRef(0);

  // Fire login_start once on mount
  useEffect(() => {
    const lastVisit = localStorage.getItem('britannia_last_visit');
    const days = lastVisit
      ? Math.floor((Date.now() - Number(lastVisit)) / 86400000)
      : null;
    trackLoginStart({ days_since_last_visit: days });
    localStorage.setItem('britannia_last_visit', String(Date.now()));
  }, []);

  useEffect(() => {
    let isMounted = true

    const redirectExistingProfile = async () => {
      const shouldRedirect = await shouldRedirectToWelcomeScreen()

      if (isMounted && shouldRedirect) {
        navigate("/welcome-back", { replace: true })
      }
    }

    redirectExistingProfile()

    return () => {
      isMounted = false
    }
  }, [navigate])

  const otpRefs = useRef([])
  const timerRef = useRef(null)

  // ── Timer ──────────────────────────────────────────────
  useEffect(() => {
    if (!timerActive) return
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          setTimerActive(false)
          setExpired(true)
          trackOtpTimeout({ time_elapsed: 120 })
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timerRef.current)
  }, [timerActive])

  function startTimer() {
    clearInterval(timerRef.current)
    setTimeLeft(120)
    setExpired(false)
    setTimerActive(true)
  }

  function formatTime(secs) {
    const m = String(Math.floor(secs / 60)).padStart(2, '0')
    const s = String(secs % 60).padStart(2, '0')
    return `${m}:${s}`
  }

  const isTamil = i18n.language === "ta";

  // ── Phone helpers ──────────────────────────────────────
  function getDigits(val) {
    return normalizeIndianMobile(val)
  }

  function handlePhoneChange(e) {
    const val = e.target.value
    if (!val.startsWith('+91')) {
      setPhone('+91 ')
      setPhoneError('')
      return
    }
    // Keep space after +91, only allow digits after it
    const afterPrefix = val.slice(4).replace(/\D/g, '').slice(0, 10)
    setPhone('+91 ' + afterPrefix)
    setPhoneError('')
  }

  // ── Send OTP ───────────────────────────────────────────
  async function handleSendOTP() {
    const digits = getDigits(phone);

    if (digits.length !== 10) {
      setPhone("");
      setPhoneError(t("auth.invalidPhone"));
      return;
    }

    try {
      const rateLimitStatus = consumeRateLimit(AUTH_RATE_LIMIT);

      if (rateLimitStatus.blocked) {
        setPhoneError(AUTH_RATE_LIMIT.message);
        return;
      }

      setLoading(true);
      const recaptchaToken = await executeRecaptcha("login");
      trackLoginSubmit({ phone_masked: `+91******${digits.slice(-4)}` });

      await api.post("/user/register", {
        mobile: digits,
        flow: "login",
        token: recaptchaToken,
      });

      trackOtpRequested();
      setOtpVisible(true);
      setOtp(["", "", "", "", "", ""]);
      setOtpError("");
      setPhoneError("");

      startTimer();

      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setPhone("");
      if (err?.response?.status === 429) {
        applyServerRateLimit(
          AUTH_RATE_LIMIT,
          err?.response?.headers?.["retry-after"]
        );
      }
      const error = handleAxiosError(err);
      console.log(error);

      // Check for specific error: "This number is not registered. Please sign-up"
      const serverMessage = err.response?.data?.message;
      if (serverMessage === "This number is not registered. Please sign-up") {
        setPhoneError(t("auth.notRegistered") || "This number is not registered.");
      } else {
        setPhoneError(error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Resend OTP ─────────────────────────────────────────
  async function handleResend() {
    const digits = getDigits(phone);

    try {
      const rateLimitStatus = consumeRateLimit(AUTH_RATE_LIMIT);

      if (rateLimitStatus.blocked) {
        setOtpError(AUTH_RATE_LIMIT.message);
        return;
      }

      await api.post("/user/resend-otp", {
        mobile: digits,
      });

      setOtp(["", "", "", "", "", ""]);
      setOtpError("");
      setSuccessMsg(`✓ ${t("auth.otpResent")}`);
      otpResendCountRef.current += 1;
      trackOtpResend({ attempt_count: otpResendCountRef.current });

      startTimer();

      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      if (err?.response?.status === 429) {
        applyServerRateLimit(
          AUTH_RATE_LIMIT,
          err?.response?.headers?.["retry-after"]
        );
        setOtpError(err?.response?.data?.message || AUTH_RATE_LIMIT.message);
        return;
      }
      setOtpError(t("auth.tryLater"));
    }
  }

  // ── OTP input handlers ─────────────────────────────────
  function handleOtpChange(e, idx) {
    if (e.target.value.length > 1) {
      // Handle paste or autofill (iOS Safari fires onChange with full code)
      handleOtpPaste(null, e.target.value);
      return;
    }
    const val = e.target.value.replace(/\D/g, '').slice(0, 1)
    const newOtp = [...otp]
    newOtp[idx] = val
    setOtp(newOtp)
    setOtpError('')
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus()
  }

  function handleOtpKeyDown(e, idx) {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus()
    }
  }

  // ── IMPROVED: cross-browser paste handler ──────────────
  // Handles: manual paste, iOS Safari autofill (onChange), Web OTP API
  function handleOtpPaste(e, value) {
    if (e && e.preventDefault) e.preventDefault();
    const raw = value
      ?? (e?.clipboardData || window.clipboardData)?.getData('text')
      ?? '';
    const pasted = raw.replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newOtp = Array(6).fill('').map((_, i) => pasted[i] || '');
    setOtp(newOtp);
    setOtpError('');
    const next = Math.min(pasted.length - 1, 5);
    otpRefs.current[next]?.focus();
  }

  // ── Submit / Verify OTP ────────────────────────────────
  // Wrapped in useCallback so the Web OTP API useEffect always gets
  // the latest version without stale closure issues
  const handleEnter = useCallback(async (prefillOtp) => {
    // prefillOtp is passed by the Web OTP API useEffect; otherwise use state
    const otpVal = (prefillOtp ?? otp).join("");
    const digits = getDigits(phone);

    if (otpVal.length < 6) {
      setOtpError(t("auth.enterAllDigits"));
      return;
    }

    try {
      const rateLimitStatus = consumeRateLimit(AUTH_RATE_LIMIT);

      if (rateLimitStatus.blocked) {
        setOtpError(AUTH_RATE_LIMIT.message);
        return;
      }

      setVerifying(true);

      const res = await api.post("/user/verify-otp", {
        mobile: digits,
        verification_code: otpVal,
      });

      console.log(res);

      // Some browsers (iOS Safari / in-app browsers) block third-party cookies.
      // If the backend returns a token in the response body, persist it so
      // subsequent requests use Authorization header as a fallback.
      const token = res?.data?.token || res?.data?.authToken || res?.data?.access_token;
      if (token) {
        localStorage.setItem("authToken", token);
      }

      clearInterval(timerRef.current);

      setLoggedIn(true);

      const createdAt = localStorage.getItem('britannia_reg_date');
      const daysSinceReg = createdAt
        ? Math.floor((Date.now() - Number(createdAt)) / 86400000)
        : null;
      const progress = JSON.parse(localStorage.getItem('britannia_kural_progress') || '[]');
      trackLoginSuccess({
        session_id: String(Date.now()),
        days_since_registration: daysSinceReg,
        kurals_in_progress: progress.length,
      });

      localStorage.setItem("isLoggedIn", "true");
      // Reset welcome-back popup flag so it can show once for this login session
      try {
        localStorage.removeItem('britannia_welcome_shown');
      } catch (e) {
        // ignore
      }

      const userDataResponse = await api.get("/user/userData", {
        withCredentials: true,
      });
      const nextFlow = resolveSecondLoginFlow(userDataResponse?.data?.data);
      setJourneyFlowStage(nextFlow.stage);
      redirectToRoute(navigate, nextFlow.route);

    } catch (err) {
      if (err?.response?.status === 429) {
        applyServerRateLimit(
          AUTH_RATE_LIMIT,
          err?.response?.headers?.["retry-after"]
        );
        setOtpError(err?.response?.data?.message || AUTH_RATE_LIMIT.message)
      } else {
        loginOtpWrongCountRef.current += 1;
        trackLoginOtpWrong({ attempt_count: loginOtpWrongCountRef.current });
        setOtpError(t("auth.wrongOtp"))
      }
    } finally {
      setVerifying(false);
    }
  }, [otp, phone, navigate, t]);

  // Keep a stable ref so the Web OTP effect never needs handleEnter as a dep
  const handleEnterRef = useRef(handleEnter);
  useEffect(() => { handleEnterRef.current = handleEnter; }, [handleEnter]);

  // ── NEW: Web OTP API — Android Chrome SMS autofill ─────
  // Fires when the OTP screen becomes visible. Chrome intercepts the
  // incoming SMS and returns the code automatically. iOS Safari and
  // desktop browsers ignore this silently.
  useEffect(() => {
    if (!otpVisible) return;
    if (!('OTPCredential' in window)) return;   // not supported → skip

    const ac = new AbortController();

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((credential) => {
        if (!credential?.code) return;

        const digits = credential.code.replace(/\D/g, '').slice(0, 6);
        const newOtp = Array(6).fill('').map((_, i) => digits[i] || '');
        setOtp(newOtp);
        setOtpError('');

        // Auto-submit only when we got all 6 digits
        if (digits.length === 6) {
          handleEnterRef.current(newOtp);  // use ref to avoid stale closure + prevent effect re-run
        }
      })
      .catch((err) => {
        console.log('Web OTP API failed or was dismissed', err);
        // User dismissed, timeout, or browser denied — no action needed
      });

    return () => ac.abort();  // cleanup if component unmounts or OTP screen hides
  }, [otpVisible]);  // ← handleEnter intentionally excluded: ref keeps it current without re-running
  

  // ── Main button handler ────────────────────────────────
  function handleMainBtn() {
    if (loading || verifying) return;

    if (!otpVisible) {
      handleSendOTP();
    } else {
      handleEnter();
    }
  }

  return (
    <div>
      <title>Log In | Britannia</title>
      <div className="page-wrapper">
        <div className="main-wrapper yellow-bg">
          {/* ══ PERSISTENT FRAME ══ */}
          <div className="frame-outer">
            {/* orange corners sit at frame-outer edges */}
            <span className="frame-corner tl" />
            <span className="frame-corner tr" />
            <span className="frame-corner bl" />
            <span className="frame-corner br" />
            {/* black outer → green → black inner → blue canvas */}
            <div className="frame-border-outer">
              <div className="frame-border">
                <div className="frame-border-inner">
                  <div className="frame-canvas">
                    <img
                      className="frame-bg-image"
                      src="assets/images/star-bg.webp"
                      alt="Star Element"
                    />
                    {/* MAIN */}
                    <div className="frame-content">
                      {/* LOG IN SCREEN START */}
                      <div className="signup-screen">
                        <div className="signup-container">
                          {/* top header */}
                          <div className="signup-header">
                            <div className="signup-logo-wrap">
                              <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="signup-logo signup-logo-1" />
                              <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="signup-logo signup-logo-2" />
                            </div>

                            <img src="assets/images/new-signup-logo.webp" alt="திருக்குறள் சேலஞ்ச்" className="signup-title-img" />
                          </div>

                          {/* scrollable form body */}
                          <div className="login-body">
                            <h2 className="signup-heading text-center">{t("auth.logIn")}</h2>
                            <div className="cashback-banner">
                              <div className="banner-content">
                                <h3
                                  style={{
                                    fontSize: isTamil ? "7px" : "10px",
                                    lineHeight: isTamil ? "1.15" : "1.3",
                                    fontWeight: 700,
                                  }}
                                >
                                  {t("banner.cashbackTitle")}
                                  <br />
                                  {t("banner.cashbackSubtitle")}
                                </h3>

                                <img
                                  src="assets/images/bicycle-icon.png"
                                  alt={t("banner.cashbackSubtitle")}
                                />
                              </div>
                            </div>
                            <form
                              className="login-form"
                              id="loginForm"
                            >
                              {/* Phone Number */}
                              <div
                                className={`form-field input-with-icon${phoneError ? " error" : ""}`}
                                id="loginPhoneField"
                              >
                                <label className="form-label">{t("auth.phoneNumberLabel")}</label>
                                <input
                                  type="tel"
                                  className="form-input phone-input"
                                  id="loginPhone"
                                  value={phone}
                                  onChange={handlePhoneChange}
                                  maxLength={14}
                                  disabled={otpVisible}
                                />
                                {phoneError && (
                                  <>
                                    <span className="error-text" style={{ display: 'block' }}>
                                      {phoneError}
                                    </span>
                                    <span className="error-icon" style={{ display: 'flex' }}>!</span>
                                  </>
                                )}
                              </div>
                              {/* OTP Label */}
                              <div className="form-field">
                                <label className="form-label">{t("auth.otp")}</label>
                              </div>

                              {/* OTP Fields */}
                              <div className="otp-wrapper">
                                {otp.map((digit, idx) => (
                                  <input
                                    key={idx}
                                    ref={el => (otpRefs.current[idx] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    className="otp-input"
                                    disabled={!otpVisible}
                                    autoComplete="one-time-code"
                                    value={digit}
                                    onChange={e => handleOtpChange(e, idx)}
                                    onKeyDown={e => handleOtpKeyDown(e, idx)}
                                    onPaste={handleOtpPaste}
                                    style={{
                                      opacity: otpVisible ? 1 : 0.5,
                                      cursor: otpVisible ? "text" : "not-allowed",
                                      ...(otpError ? { borderColor: "#ff4444" } : {}),
                                    }}
                                  />
                                ))}
                              </div>

                              {/* Timer Row */}
                              <div className="otp-timer-row" id="loginOtpTimerRow">
                                {otpError && (
                                  <span className="otp-error-text" style={{ display: 'inline' }}>
                                    {otpError}
                                  </span>
                                )}
                                {!expired && (
                                  <>
                                    <span className="otp-expire-text">{t("auth.codeExpire")}</span>
                                    <span className="otp-timer" id="otpTimer">
                                      {formatTime(timeLeft)}
                                    </span>
                                  </>
                                )}
                                {expired && (
                                  <a
                                    id="loginOtpResendLink"
                                    className="otp-resend-link"
                                    style={{ display: 'inline', cursor: 'pointer' }}
                                    onClick={handleResend}
                                  >
                                    {t("auth.resendCode")}
                                  </a>
                                )}
                              </div>

                              {/* reCAPTCHA */}
                              {/* <div className="form-field recaptcha-field">
                                <div className="recaptcha-placeholder">Protected by reCAPTCHA</div>
                              </div> */}



                              {/* Button */}
                              {loggedIn ? (
                                <button
                                  className="btn-next"
                                  style={{ marginTop: "40px", background: "#ffc100", opacity: 1 }}
                                  type="button"
                                  disabled
                                >
                                  <span>{t("auth.loggedIn")}</span>
                                </button>
                              ) : (
                                <button
                                  className="btn-next"
                                  style={{
                                    "marginTop": "12px"
                                  }}
                                  type="button"
                                  onClick={handleMainBtn}
                                  disabled={loading || verifying}
                                >
                                  <span style={{
                                    fontSize: language === "ta" ? "13px" : "18px"
                                  }}>
                                    {loading
                                      ? t("auth.sendingOtp")
                                      : verifying
                                        ? t("common.verifying")
                                        : otpVisible
                                          ? t("common.enter")
                                          : t("common.enter")}
                                  </span>
                                </button>

                              )}
                              <p className="signup-login">
                                {t("auth.dontHaveAccount")} <Link to="/register">{t("auth.signUpLink")}</Link>
                              </p>
                            </form>
                          </div>
                        </div>
                      </div>
                      {/* LOG IN SCREEN END */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

    </div>

  )
}

export default Login